/**
 * Servicio de caché offline para el dashboard del supervisor.
 * Almacena estadísticas, actividades, inspectores y datos de planta
 * en IndexedDB para acceso sin conexión.
 */

const DB_NAME = "DarphaOfflineDashboard";
const DB_VERSION = 1;

const STORES = {
  DASHBOARD: "dashboardData",
  SYNC_META: "dashboardSyncMeta",
};

class OfflineDashboardCache {
  async _openDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORES.DASHBOARD)) {
          db.createObjectStore(STORES.DASHBOARD, { keyPath: "key" });
        }
        if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
          db.createObjectStore(STORES.SYNC_META, { keyPath: "key" });
        }
      };
    });
  }

  async _put(storeName, data) {
    const db = await this._openDB();
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const req = store.put(data);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        db.close();
        resolve();
      };
    });
  }

  async _get(storeName, key) {
    const db = await this._openDB();
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    return new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        db.close();
        resolve(req.result?.data ?? null);
      };
    });
  }

  /**
   * Guarda un snapshot completo del dashboard del supervisor
   */
  async saveDashboard(plantaId, snapshot) {
    const cacheKey = `dashboard-${plantaId}`;
    try {
      await this._put(STORES.DASHBOARD, {
        key: cacheKey,
        data: snapshot,
        cachedAt: Date.now(),
      });
      await this._put(STORES.SYNC_META, {
        key: cacheKey,
        timestamp: Date.now(),
      });
      console.log(`[OfflineDashboard] Dashboard guardado (planta ${plantaId})`);
    } catch (error) {
      console.error("[OfflineDashboard] Error guardando:", error);
    }
  }

  /**
   * Recupera el snapshot del dashboard desde caché
   */
  async getDashboard(plantaId) {
    try {
      return await this._get(STORES.DASHBOARD, `dashboard-${plantaId}`);
    } catch (error) {
      console.error("[OfflineDashboard] Error leyendo:", error);
      return null;
    }
  }

  /**
   * Obtiene info de última sincronización
   */
  async getSyncInfo(plantaId) {
    try {
      const db = await this._openDB();
      const tx = db.transaction(STORES.SYNC_META, "readonly");
      const store = tx.objectStore(STORES.SYNC_META);
      return new Promise((resolve, reject) => {
        const req = store.get(`dashboard-${plantaId}`);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => {
          db.close();
          resolve(req.result || null);
        };
      });
    } catch (error) {
      console.error("[OfflineDashboard] Error leyendo sync info:", error);
      return null;
    }
  }
}

const offlineDashboardCache = new OfflineDashboardCache();
export default offlineDashboardCache;
